import { Formik } from 'formik';
import type { FormikConfig, FormikHelpers } from 'formik/dist/types';
import { SnackbarProvider } from 'notistack';
import React, { useMemo } from 'react';

import { ScrollFirstError } from '../../components/ScrollFirstError/ScrollFirstError';

export type FormProps<Values> = {
  initialValues?: Values;
  onSubmit?: (values: Values, formikHelpers: FormikHelpers<Values>) => Promise<void> | void;
  children?: React.ReactNode;
  maxSnack?: number;
  validate?: FormikConfig<Values>['validate'];
  validationSchema?: FormikConfig<Values>['validationSchema'];
};

/**
 * Form Provider
 */
export function Form<Values>({
  children,
  onSubmit = () => {
    return;
  },
  initialValues = {} as Values,
  maxSnack = 3,
  validate,
  validationSchema,
}: FormProps<Values>): React.ReactElement {
  // required to fix a formik bug that calls validate with the first initialValues as soon as the initialValues are changed.
  // https://github.com/jaredpalmer/formik/issues/2046
  const frozenInitialValues = useMemo(() => initialValues, []);

  return (
    <Formik
      initialValues={frozenInitialValues}
      onSubmit={onSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      validate={validate}
      validationSchema={validationSchema}
    >
      <SnackbarProvider maxSnack={maxSnack}>
        <ScrollFirstError />
        {children}
      </SnackbarProvider>
    </Formik>
  );
}
